import { useCallback, useMemo } from 'react';
import { Link } from 'react-router';

import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { buildPaymentRequestColumns } from '#/modules/finMgmt/claims/data/paymentRequestColumns';
import { getPaymentRequestApiErrorMessage } from '#/modules/finMgmt/claims/helper/apiError';
import { PAYMENT_REQUEST_LIST_SORT_FIELD_LABELS } from '#/modules/finMgmt/claims/helper/paymentRequestStatus';
import usePaymentRequestList from '#/modules/finMgmt/claims/hooks/usePaymentRequestList';
import {
  applyTotalAmountRange,
  TOTAL_AMOUNT_FILTER_STATUS_FIELDS,
} from '#/modules/finMgmt/shared/helper/fmsListRangeFilters';
import { useCan } from '#/modules/infra/auth/useCan';

/**
 * 個人請款列表面板（嵌於個人中心右側；server-side list，無 SideMsg）。
 */
const MyClaimsPanel = () => {
  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  } = usePaymentRequestList({ scope: FMS_READ_SCOPE.SELF });
  const canCreate = useCan(PERM.FMS_PAYMENT_REQUEST_CREATE);

  const columns = useMemo(
    () => buildPaymentRequestColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      onTotalAmountRangeChange: (range) => applyTotalAmountRange(setFilter, range),
    }),
    [listQuery.filters, setFilter],
  );

  const filterStatusFields = useMemo(
    () => [
      ...buildFilterStatusFieldsFromColumns(columns, {
        filters: listQuery.filters,
      }),
      ...TOTAL_AMOUNT_FILTER_STATUS_FIELDS,
    ],
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const headerAction = canCreate ? (
    <Button type="button" asChild>
      <Link to={ROUTES.personalCenterClaimCreate}>新增請款</Link>
    </Button>
  ) : null;

  return (
    <div
      className={cn(
        'flex min-h-0 w-full flex-col',
        'h-[min(52rem,calc(100vh-10rem))]',
      )}
    >
      <ListTablePanel
        tableKey="fms.paymentRequest.personal.list"
        columns={columns}
        rows={rows}
        pagination={pagination}
        isLoading={isLoading}
        isError={isError}
        errorMessage={
          isError
            ? getPaymentRequestApiErrorMessage(error, '載入請款失敗')
            : undefined
        }
        onRetry={isError ? () => { refetch(); } : undefined}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearch={setKeyword}
        onReset={resetListQuery}
        initialKeyword={listQuery.keyword}
        searchPlaceholder="搜尋單號、事由或備註"
        headerAction={headerAction}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={PAYMENT_REQUEST_LIST_SORT_FIELD_LABELS}
        onFilterChange={setFilter}
        onKeywordClear={() => setKeyword('')}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        panelClassName="min-h-0 flex-1"
      />
    </div>
  );
};

export default MyClaimsPanel;
